import { add, remove, init, setUserId, Identify, identify, track, setOptOut } from '@amplitude/analytics-browser';
import { isLocalhost, assert } from '@faro-lotv/foundation';
import { useEffect, useState, createContext, useMemo, useContext, useCallback } from 'react';
import { jsx, Fragment } from 'react/jsx-runtime';
import { IntercomProvider as IntercomProvider$1, useIntercom as useIntercom$1 } from 'react-use-intercom';
import { CookiesProvider, useCookies } from 'react-cookie';

/**
 * The client to handle the amplitude events and report them
 */
class AmplitudeClient {
    /**
     * Add a plugin to the client, which can intercept, extend and modify events.
     * Ideally, this is called before `init` to ensure that the plugin can intercept all events.
     *
     * @param plugin The plugin to add to the client.
     * @returns A promise with the result of the adding the plugin.
     * @see https://amplitude.com/docs/sdks/analytics/browser/browser-sdk-2#plugins
     */
    add = add;
    /**
     * @param pluginName The name of the plugin to remove.
     * @returns A promise with the result of the removing the plugin.
     */
    remove = remove;
    /**
     * Initialize Amplitude analytics with the given project API key
     *
     * @param apiKey API Key of the amplitude analytics project
     * @param props Initial props to set the configurations of the amplitude
     * @returns A promise from the init function of Amplitude
     * @see https://developers.amplitude.com/docs/typescript-browser#initializing-sdk
     */
    init(apiKey, props) {
        if (props) {
            return init(apiKey, props.userId, {
                ...props.initConfigs,
            });
        }
        return init(apiKey);
    }
    /**
     * Sets the user ID in the amplitude analytics
     *
     * @param userId The ID of the user
     */
    set userId(userId) {
        setUserId(userId);
    }
    /**
     * Sets the properties related to the current user
     */
    set userProperties(userProps) {
        const identifyObj = new Identify();
        for (const key in userProps) {
            identifyObj.set(key, userProps[key]);
        }
        identify(identifyObj);
    }
    /**
     * sets the property related to the current user
     *
     * @deprecated @see userProperties instead
     */
    set userProperty({ propertyName, propertyValue }) {
        const identifyObj = new Identify();
        identifyObj.set(propertyName, propertyValue);
        identify(identifyObj);
    }
    /**
     * Tracks an event of a certain Type with an optional additional details
     *
     * @param type Type of the event to be recorded
     * @param properties Additional details related to the event
     * @returns A promise with the result of the tracking event
     * @see https://developers.amplitude.com/docs/typescript-browser#tracking-an-event
     */
    trackEvent(type, properties) {
        // In case one wants to check the status of event logging, the track function does return a promise
        return track(type, properties);
    }
    /**
     * Enable/disable the analytics tracking of the user
     *
     * @param shouldOptOut True if the user wants to opt out of analytical tracking
     * @see https://developers.amplitude.com/docs/typescript-browser#tracking-switch
     */
    set optOutOfAnalytics(shouldOptOut) {
        setOptOut(shouldOptOut);
    }
}

const CENSOR_PRIVATE_DATA_PLUGIN_NAME = "censor-private-data";
/**
 * Module to provide analytics functionality
 *
 * Currently only provides a wrapper around AmplitudeClient
 * but might be extended to use different analytics services in the future
 */
class AnalyticsService {
    #client = new AmplitudeClient();
    #defaultProps = {};
    #userProperties = {};
    #privateData = undefined;
    async init(apiKey, props = {}) {
        try {
            await this.#client.init(apiKey, props).promise;
        }
        catch (error) {
            // eslint-disable-next-line no-console
            console.error("Amplitude client could not be initialized", { error });
        }
    }
    /**
     * Send an event to the analytics service
     *
     * @param eventName - Name of the the event being tracked
     * @param eventProps - Props of the event being tracked
     * @returns - A promise with the result of tracking event
     */
    track(eventName, eventProps) {
        // Updating the screen dimensions before each track call to attach the current sizes to the event
        this.#client.userProperties = {
            ...this.#userProperties,
            screenWidth: window.screen.width,
            windowWidth: window.innerWidth,
            screenHeight: window.screen.height,
            windowHeight: window.innerHeight,
            // screen.orientation() is not supported in iOS Safari 15.6-15.8, hence the optional chaining
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition, compat/compat
            windowOrientation: window.screen.orientation?.type,
        };
        return this.#client.trackEvent(eventName, {
            ...eventProps,
            ...this.#defaultProps,
        });
    }
    /**
     * Enable/disable the analytics tracking of the user
     *
     * @param shouldOptOut True if the user wants to opt out of analytical tracking
     * @see https://developers.amplitude.com/docs/typescript-browser#tracking-switch
     */
    set optOutOfAnalytics(shouldOptOut) {
        this.#client.optOutOfAnalytics = shouldOptOut;
    }
    /**
     * Set the props that will be attached to every tracking event
     */
    set props({ defaultProps: newDefaultProps, userId, userProps, }) {
        if (newDefaultProps) {
            this.#defaultProps = { ...newDefaultProps };
        }
        if (userProps) {
            this.#userProperties = userProps;
            this.#client.userProperties = this.#userProperties;
        }
        if (userId) {
            this.#client.userId = userId;
        }
    }
    /**
     * Set the private data that should be censored from the event properties.
     *
     * This is accomplished with an Amplitude enrichment plugin that intercepts all events.
     *
     * The private data should be set _before_ initializing the analytics client, otherwise some events might not be censored.
     */
    set privateData(privateData) {
        if (this.#privateData) {
            this.#client.remove(CENSOR_PRIVATE_DATA_PLUGIN_NAME);
        }
        this.#privateData = privateData;
        this.#client.add(censorPrivateDataPlugin(this.#privateData));
    }
    /** @returns The currently assigned private data which will be censored. */
    get privateData() {
        return this.#privateData;
    }
}
/**
 * Remove private data from the page title of the built-in page view events.
 *
 * @param privateData The private data that should be removed from the page title.
 *  This must be a map from the name of the data, to the current data value.
 * @returns An enrichment plugin that removes the private data from the page view events.
 */
function censorPrivateDataPlugin(privateData) {
    return {
        name: CENSOR_PRIVATE_DATA_PLUGIN_NAME,
        type: "enrichment",
        execute: (event) => {
            if (
            // For now, only censor data in the built-in page view events
            event.event_type !== "[Amplitude] Page Viewed" ||
                !privateData ||
                Object.keys(privateData).length === 0 ||
                !event.event_properties) {
                // If there is nothing to censor, just forward the event
                return Promise.resolve(event);
            }
            const censoredEventProperties = event.event_properties ?? {};
            let censoredPageTitle = censoredEventProperties["[Amplitude] Page Title"];
            if (typeof censoredPageTitle === "string") {
                // Replace all private data with placeholders
                for (const [name, data] of Object.entries(privateData)) {
                    if (data) {
                        censoredPageTitle = censoredPageTitle.replaceAll(data, `{{${name}}}`);
                    }
                }
                censoredEventProperties["[Amplitude] Page Title"] = censoredPageTitle;
            }
            const censoredEvent = {
                ...event,
                event_properties: censoredEventProperties,
            };
            return Promise.resolve(censoredEvent);
        },
    };
}
const Analytics = new AnalyticsService();

/**
 * Initialize the analytics client for the application unless the user is from either HoloBuilder or Faro
 *
 * @param key amplitude key
 * @param currentUserEmail current user email if an user is logged in
 * @param companyId Optional ID of the company to attach to every event
 * @param privateData Optional private data that should be censored from the event properties
 *  This should be a stable reference to avoid unnecessary plugin removals and additions
 */
function useAnalyticsInitialization(key, currentUserEmail, companyId, privateData) {
    // Censor private data from all events
    // The private data needs to be updated _before_ the analytics client is initialized
    // Otherwise some events might not be censored
    useEffect(() => {
        Analytics.privateData = privateData;
    }, [privateData]);
    // Add company ID to user data
    useEffect(() => {
        const props = companyId ? { userProps: { companyId } } : undefined;
        if (props !== undefined) {
            Analytics.props = { ...props };
        }
    }, [companyId]);
    // Initialize Amplitude client
    // This will also send the built-in page view event
    useEffect(() => {
        // Exclude @faro.com and @holobuilder.com and anonymous users from the analytics
        // we can't track anonymous users without a consent dialog that we're not implementing
        if (!key || isInternalUserEmail(currentUserEmail)) {
            return;
        }
        // Store data in local storage instead of cookies in development environments to avoid failed requests
        // caused by a too large cookie header.
        const { hostname } = globalThis.location;
        const identityStorage = isLocalhost() ||
            hostname.includes(".dev.") ||
            hostname.includes(".staging.")
            ? "localStorage"
            : "cookie";
        Analytics.init(key, {
            initConfigs: { identityStorage },
        });
    }, [currentUserEmail, key]);
}
/**
 * check if user email is from @faro.com or @holobuilder.com or anonymous
 *
 * @param userEmail the given user email
 * @returns true if the given usr email is from @faro.com or @holobuilder.com or anonymous, false otherwise
 */
function isInternalUserEmail(userEmail) {
    return (!userEmail ||
        userEmail.includes("@faro.com") ||
        userEmail.includes("@holobuilder.com"));
}

/**
 * Loading an external script
 *
 * @param scriptUrl The URL of the script to load
 * @returns the state of the loading process
 */
function useExternalScript(scriptUrl) {
    const [isDoneLoading, setIsDoneLoading] = useState(false);
    useEffect(() => {
        if (scriptUrl) {
            const script = document.createElement("script");
            script.src = scriptUrl;
            script.async = false;
            script.addEventListener("load", () => setIsDoneLoading(true));
            document.body.appendChild(script);
            return () => {
                document.body.removeChild(script);
            };
        }
    }, [scriptUrl]);
    return { isDoneLoading };
}

const HBCookieManagerContext = createContext(undefined);
/**
 * @returns The context that provides access to the HB cookie-manager.
 *  Will load the provided script URL and then call the init function of the cookie-manager.
 *  Note: The init call will show the UI of the cookie-manager if the user didn't interact with it yet.
 */
function HBCookieManagerProvider({ children, cookieManagerUrl, options = {}, }) {
    // Load the main cookie manager script
    const { isDoneLoading } = useExternalScript(cookieManagerUrl);
    const [isInitialized, setIsInitialized] = useState(false);
    // Initialize the cookie-manager once the script has been loaded
    useEffect(() => {
        if (isDoneLoading && window.HolobuilderCookieManager && !isInitialized) {
            window.HolobuilderCookieManager.init(options);
            setIsInitialized(true);
        }
    }, [isDoneLoading, options, isInitialized]);
    const value = useMemo(() => ({
        isManagerLoaded: isDoneLoading,
        isInitialized,
        isManagerUsed: !!cookieManagerUrl,
    }), [isDoneLoading, isInitialized, cookieManagerUrl]);
    return (jsx(HBCookieManagerContext.Provider, { value: value, children: children }));
}

/**
 * @returns The HolobuilderCookieManager instance
 *
 * Note: kept only local in order to not expose too much of the cookie-manager outside the package
 */
function useHBCookieManager() {
    const cookieManagerContext = useContext(HBCookieManagerContext);
    assert(cookieManagerContext, "useCookieManager is only available inside a CookieManagerProvider");
    if (!window.HolobuilderCookieManager && cookieManagerContext.isManagerUsed) {
        // Having a debug message in the logs will allow devs to understand why the cookie-manager might not work as expected
        // eslint-disable-next-line no-console
        console.debug("cookie-manager is not available yet");
    }
    return window.HolobuilderCookieManager;
}
/**
 * @returns True if the cookie-manager has been initialized (i.e., all functionality is available)
 */
function useIsCookieManagerInitialized() {
    const cookieManagerContext = useContext(HBCookieManagerContext);
    assert(cookieManagerContext, "useIsCookieManagerInitialized is only available inside a CookieManagerProvider");
    return cookieManagerContext.isInitialized;
}
/**
 * @returns A method for checking if a specific cookie is accepted
 */
function useIsCookieAccepted() {
    const cookieManagerContext = useContext(HBCookieManagerContext);
    assert(cookieManagerContext, "useIsCookieAccepted is only available inside a CookieManagerProvider");
    const cookieManager = useHBCookieManager();
    return useCallback((id) => {
        if (!cookieManager)
            return false;
        return cookieManager.isCookieAccepted(id);
    }, [cookieManager]);
}
/**
 * Frequency of cookie updates via polling in ms.
 * Needs to be fast enough to guarantee a smooth experience after cookies have been accepted.
 */
const REACTIVE_UPDATE_INTERVAL = 2000;
/**
 * A version of useIsCookieAccepted that provides a reactive boolean value.
 * Uses a timer internally to update the value.
 *
 * Use e.g. when you want to dynamically enable/disable a component based on the cookie state.
 *
 * @param id The cookie id to check
 * @returns Whether the cookie is accepted
 */
function useIsCookieAcceptedReactive(id) {
    const isCookieAccepted = useIsCookieAccepted();
    const [isAccepted, setIsAccepted] = useState(isCookieAccepted(id));
    useEffect(() => {
        const interval = setInterval(() => {
            const newAccepted = isCookieAccepted(id);
            if (newAccepted !== isAccepted)
                setIsAccepted(newAccepted);
        }, REACTIVE_UPDATE_INTERVAL);
        return () => clearInterval(interval);
    }, [id, isAccepted, isCookieAccepted]);
    return isAccepted;
}
/**
 * @returns A method for opening the cookie-manager
 */
function useShowCookieManager() {
    const cookieManagerContext = useContext(HBCookieManagerContext);
    assert(cookieManagerContext, "useIsCookieAccepted is only available inside a CookieManagerProvider");
    assert(cookieManagerContext.isManagerUsed, "useShowCookieManager is only available when the cookie-manager is used");
    const cookieManager = useHBCookieManager();
    return useCallback(() => cookieManager?.show?.(), [cookieManager]);
}
/**
 * @returns A method for opening the cookie-manager to accept a specific cookie, or undefined if the cookie-manager is not available.
 */
function useShowAcceptCookieDialog() {
    const cookieManagerContext = useContext(HBCookieManagerContext);
    assert(cookieManagerContext, "useShowAcceptCookieDialog is only available inside a CookieManagerProvider");
    const cookieManager = useHBCookieManager();
    // The cookie manager showAcceptCookieDialog function is not available immediately in the API.
    // It is added dynamically during the cookie-manager's initialization.
    // Here we make sure to only access it through the cookieManager instance, and to not save a stale reference to it.
    const acceptFunctionInternal = useCallback((cookieId) => {
        assert(cookieManager?.showAcceptCookieDialog, "useShowAcceptCookieDialog was used before the cookie-manager is ready");
        cookieManager.showAcceptCookieDialog({
            cookieId,
        });
    }, [cookieManager]);
    return cookieManagerContext.isManagerUsed
        ? acceptFunctionInternal
        : undefined;
}

/** All known IDs of cookies available through the cookie-manager */
var CookieId;
(function (CookieId) {
    CookieId["intercom"] = "intercom";
    CookieId["youtube"] = "youtube";
    CookieId["amplitude"] = "amplitude";
})(CookieId || (CookieId = {}));

/** A dialog context which provides the components with the function to create a dialog */
const IntercomContext = createContext(undefined);
/**
 *  @returns A context provider to allow usage of intercom
 */
function IntercomProvider({ children, enabled, appId, user, onRequestEnable, }) {
    const [unreadMessagesCount, setUnreadMessagesCount] = useState(0);
    if (!appId) {
        // eslint-disable-next-line react/jsx-no-useless-fragment
        return jsx(Fragment, { children: children });
    }
    return (jsx(IntercomProvider$1, { appId: appId, onUnreadCountChange: setUnreadMessagesCount, 
        // Avoid initializing intercom, if it is not enabled. This avoids console warnings and a request to Intercom servers.
        shouldInitialize: enabled, 
        // We manually boot and shutdown intercom through the internal context below
        autoBoot: false, 
        // A small initialization delay prevents a console warning about missing app ids in the dev environment.
        // see https://github.com/devrnt/react-use-intercom/issues/673
        initializeDelay: 2000, children: jsx(IntercomProviderInternal, { enabled: enabled, unreadMessagesCount: unreadMessagesCount, user: user, onRequestEnable: onRequestEnable, children: children }) }));
}
function IntercomProviderInternal({ enabled, unreadMessagesCount, user, onRequestEnable, children, }) {
    const { boot, hardShutdown, update, show: showInternal, hide, } = useIntercom$1();
    const [isBooted, setIsBooted] = useState(false);
    const [hasUserRequestedOpen, setHasUserRequestedOpen] = useState(false);
    // Enables/Disables intercom, when the enabled flag changes.
    useEffect(() => {
        if (enabled) {
            boot({
                hideDefaultLauncher: true,
            });
        }
        else {
            hardShutdown();
        }
        setIsBooted(enabled);
        // The boot function should only be called when the enabled flag changes
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [enabled]);
    // Updates the intercom user
    useEffect(() => {
        if (isBooted) {
            update({
                userId: user?.userId,
                userHash: user?.userHash,
            });
        }
    }, [user?.userId, user?.userHash, update, isBooted]);
    const show = useCallback(async () => {
        if (!enabled) {
            const shouldOpenAfterLoad = await onRequestEnable?.();
            setHasUserRequestedOpen(shouldOpenAfterLoad ?? false);
            return;
        }
        showInternal();
    }, [enabled, onRequestEnable, showInternal]);
    // Effect to show intercom once it is enabled, after the user requested it.
    useEffect(() => {
        if (hasUserRequestedOpen && enabled && isBooted) {
            showInternal();
            setHasUserRequestedOpen(false);
        }
    }, [enabled, hasUserRequestedOpen, isBooted, showInternal]);
    const value = useMemo(() => ({ unreadMessagesCount, showMessenger: show, hideMessenger: hide }), [unreadMessagesCount, show, hide]);
    return (jsx(IntercomContext.Provider, { value: value, children: children }));
}
/**
 * @returns Hook that provides access to the intercom context. Can be undefined, if Intercom is not available in the current environment.
 */
function useIntercom() {
    return useContext(IntercomContext);
}

/**
 * @returns A Provider of the IntercomContext, that is integrated with the cookie-manager from foreign-observers.
 */
function IntercomProviderWithCookieIntegration({ children, ...intercomProviderProps }) {
    const isCookieAccepted = useIsCookieAcceptedReactive(CookieId.intercom);
    const requestAcceptCookie = useShowAcceptCookieDialog();
    return (jsx(IntercomProvider, { enabled: isCookieAccepted, onRequestEnable: () => {
            assert(requestAcceptCookie, "Expected Intercom to only be shown, when cookie-manager is available.");
            requestAcceptCookie(CookieId.intercom);
            // Assume that the cookie was accepted here, and automatically open Intercom as soon as its loaded.
            return true;
        }, ...intercomProviderProps, children: children }));
}

/** CSS class to be used in order to disable translations for parts of the UI */
const NO_TRANSLATE_CLASS = "notranslate";
// Name to use for the localize specific cookie
const COOKIE_NAME = "localize-language";
const DEFAULT_LANGUAGE = { code: "en", name: "English" };
// One year in seconds
const COOKIE_MAX_AGE = 31_536_000;
/**
 * @returns The second- and top-level domain part of the URL (e.g., ".holobuilder.com" from "viewer.holobuilder.com")
 *  Using this kind of domain for the cookie will make it accessible for all subdomains that share this part of the domain
 *
 *  Uses undefined for local development
 */
function getCookieDomain() {
    // Don't set domain on localhost
    if (isLocalhost())
        return;
    const { hostname } = window.location;
    const domain = hostname
        .split(".")
        // Getting the last two entries of the array
        // eslint-disable-next-line @typescript-eslint/no-magic-numbers
        .slice(-2)
        .join(".");
    return `.${domain}`;
}
const LocalizeContext = createContext(undefined);
/**
 * @returns The context that provides access to localize object.
 *  Will load the provided script URL and then call the init function of localize.
 *
 * It wraps the internal localize provider with the cookies provider to give the internal provider access to the cookies.
 */
function LocalizeProvider({ children, projectKey, initOptions, }) {
    return (jsx(CookiesProvider, { defaultSetOptions: {
            maxAge: COOKIE_MAX_AGE,
            domain: getCookieDomain(),
            path: "/",
        }, children: jsx(LocalizeProviderIntern, { projectKey: projectKey, initOptions: initOptions, children: children }) }));
}
function LocalizeProviderIntern({ children, projectKey, initOptions, }) {
    /** List of available languages */
    const [availableLanguages, setAvailableLanguages] = useState([]);
    const [currentLanguage, setCurrentLanguage] = useState(DEFAULT_LANGUAGE);
    // Load the main Localize script to a specific version
    const { isDoneLoading } = useExternalScript("https://global.localizecdn.com/localize.js");
    const Localize = useMemo(() => (isDoneLoading ? window.Localize : undefined), [isDoneLoading]);
    const [isInitialized, setIsInitialized] = useState(false);
    const [cookies, setCookie] = useCookies([COOKIE_NAME]);
    const cookieValue = useMemo(() => cookies[COOKIE_NAME], [cookies]);
    /**
     * Storing the new language in various places to make sure the logic is working as expected:
     *  - Via Localize.setLanguage for allowing Localize to store the language in local storage to work
     *  - In the local currentLanguage state to allow the app to work
     *  - In the cookie to allow other apps to access the user-selected language
     */
    const setLanguage = useCallback((newLanguage) => {
        if (!isInitialized || !Localize)
            return;
        const currentLanguage = availableLanguages.find((language) => language.code === newLanguage);
        if (currentLanguage) {
            Localize.setLanguage(currentLanguage.code);
            setCurrentLanguage(currentLanguage);
            // Only update cookie in case the new language is not stored there yet
            // Otherwise, this could lead to an infinite loop
            if (cookieValue !== currentLanguage.code) {
                setCookie(COOKIE_NAME, currentLanguage.code);
            }
        }
    }, [availableLanguages, isInitialized, setCookie, cookieValue, Localize]);
    const translate = useCallback((input, variables) => {
        if (isInitialized && Localize) {
            return Localize.translate(input, variables);
        }
        return input;
    }, [isInitialized, Localize]);
    // Initialize the localize object once the script has been loaded,
    // and store available languages
    useEffect(() => {
        if (!isInitialized && Localize) {
            Localize.initialize({
                key: projectKey,
                rememberLanguage: true,
                retranslateOnNewPhrases: true,
                translateTitle: false,
                disableWidget: true,
                blockedClasses: [NO_TRANSLATE_CLASS],
                ...initOptions,
            });
            // Store the available languages
            Localize.getAvailableLanguages((err, languages) => {
                if (err) {
                    // eslint-disable-next-line no-console
                    console.warn("Could not get available languages");
                    return;
                }
                setAvailableLanguages(languages);
            });
            setIsInitialized(true);
        }
    }, [projectKey, initOptions, isInitialized, Localize]);
    // Update current language based on user settings (cookie or localize-stored value)
    useEffect(() => {
        if (!isInitialized || !Localize)
            return;
        // Prioritize domain-wide cookie over local storage (which is only valid for the current sub-domain)
        const languageToUse = cookieValue ?? Localize.getLanguage();
        const currentLanguage = availableLanguages.find((language) => language.code === languageToUse);
        if (currentLanguage) {
            setLanguage(currentLanguage.code);
        }
    }, [isInitialized, availableLanguages, cookieValue, Localize, setLanguage]);
    const value = useMemo(() => ({
        isScriptLoaded: isDoneLoading,
        isInitialized,
        availableLanguages,
        setLanguage,
        currentLanguage,
        translate,
    }), [
        availableLanguages,
        currentLanguage,
        isDoneLoading,
        isInitialized,
        setLanguage,
        translate,
    ]);
    return (jsx(LocalizeContext.Provider, { value: value, children: children }));
}
/**
 * @returns Hook that returns the utility function to create a dialog
 */
function useLocalize() {
    const ctx = useContext(LocalizeContext);
    if (!ctx) {
        throw Error("useLocalize called outside the LocalizeContext");
    }
    return ctx;
}
/**
 * @returns The currently selected language used by localizejs.
 *  Will return "en" in case localize is not yet initialized.
 */
function useLocalizeLanguage() {
    try {
        return useLocalize().currentLanguage;
    }
    catch {
        // Return default language in case the context is not there
        return DEFAULT_LANGUAGE;
    }
}
/** @returns a function for manual translation or a fallback, if the localization service is not available */
function useTranslate() {
    const ctx = useContext(LocalizeContext);
    if (!ctx) {
        // If the localization service is not available return the untranslated input
        return (input) => input;
    }
    return ctx.translate;
}

export { Analytics, CookieId, HBCookieManagerContext, HBCookieManagerProvider, IntercomContext, IntercomProvider, IntercomProviderWithCookieIntegration, LocalizeContext, LocalizeProvider, NO_TRANSLATE_CLASS, isInternalUserEmail, useAnalyticsInitialization, useIntercom, useIsCookieAccepted, useIsCookieAcceptedReactive, useIsCookieManagerInitialized, useLocalize, useLocalizeLanguage, useShowAcceptCookieDialog, useShowCookieManager, useTranslate };
